import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ProjectsService } from 'src/app/projects/services/projects.service';

export enum NotificationFrequency {
  CUSTOM_INTERVAL = 'CUSTOM_INTERVAL',
  EVERY_30_MIN = 'EVERY_30_MIN',
  EVERY_60_MIN = 'EVERY_60_MIN',
  EVERY_2_HOURS = 'EVERY_2_HOURS',
  DAILY = 'DAILY'
}

@Component({
  selector: 'app-notification-settings-modal',
  templateUrl: './notification-settings-modal.component.html',
  styleUrls: ['./notification-settings-modal.component.scss']
})
export class NotificationSettingsModalComponent {
  @Input() projectId?: string | null = null;
  @Input() token?: string | null = null;
  @Input() sharedLinkId?: string | null = null;
  @Input() exitIcon!: string;
  @Input() exclamationMarkCircleIcon!: string;
  
  // This input will control the modal visibility from parent
  @Input() isModalOpen = false;
  
  // Output event when modal is closed from within the component
  @Output() modalClosed = new EventEmitter<void>();
  @Output() notificationUpdated = new EventEmitter<any>();
  
  public notificationSettingsLoading = false;
  public notifications: any = {};
  public errorAlert!: string;
  public updateAlert!: string;
  NotificationFrequency = NotificationFrequency;
  
  public notificationFrequencies: NotificationFrequency[] = Object.keys(NotificationFrequency)
    .filter(key => isNaN(Number(key)))
    .map(key => NotificationFrequency[key as keyof typeof NotificationFrequency]);
  
  notificationForm = new FormGroup({
    customIntervalMinutes: new FormControl<number | null>(null),
    dailyTime: new FormControl<Date | null>(null),
    frequency: new FormControl<NotificationFrequency | null>(null),
    isEnabled: new FormControl<boolean>(false)
  });
  
  constructor(
    private projectsService: ProjectsService,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {}
  
  ngOnInit(): void {
    this.translate
      .get([
        'alert.error',
        'alert.update'
      ])
      .subscribe((translations: { [key: string]: string }) => {
        this.errorAlert = translations['alert.error'];
        this.updateAlert = translations['alert.update']
      });
      
    this.getNotifications();
    
    this.notificationForm.get('frequency')?.valueChanges.subscribe((selectedFrequency) => {
      const customIntervalControl = this.notificationForm.get('customIntervalMinutes');
      
      if (selectedFrequency === NotificationFrequency.CUSTOM_INTERVAL) {
        if (!customIntervalControl?.hasValidator(Validators.required)) {
          customIntervalControl?.setValidators([Validators.required, Validators.min(1)]);
          customIntervalControl?.updateValueAndValidity({ emitEvent: false });
        }
      } else {
        customIntervalControl?.clearValidators();
        customIntervalControl?.updateValueAndValidity({ emitEvent: false });
      }
    });
  }
  
  closeModal() {
    this.modalClosed.emit();
  }
  
  onChangeNotificationSettings() {
    this.notificationForm.markAllAsTouched();
  
    if (this.notificationForm.invalid) {
      return;
    }

    const data = this.notificationForm.getRawValue();
    let body: any = {};
  
    if (!data.isEnabled) {
      body = { 
        isEnabled: false
      };
    } else {
      body.isEnabled = true;
  
      if (data.frequency === NotificationFrequency.DAILY) {
        body.frequency = NotificationFrequency.DAILY;
        body.dailyTime = data.dailyTime;
      } else if (data.frequency === NotificationFrequency.CUSTOM_INTERVAL) {
        body.customIntervalMinutes = data.customIntervalMinutes;
      } else {
        body.frequency = data.frequency;
      }
    }
    
    this.notificationSettingsLoading = true;
    this.projectsService.updateNotificationSettings(this.token!, this.projectId!, body).subscribe({
      next: (response) => {
        this.notificationSettingsLoading = false;
        this.getNotifications();
        this.closeModal();
        this.toastr.success(this.updateAlert, '', {
          positionClass: 'toast-top-center',
        });
        this.notificationUpdated.emit(response);
      },
      error: () => {
        this.notificationSettingsLoading = false;
        this.closeModal();
        this.toastr.error(this.errorAlert, '', {
          positionClass: 'toast-top-center',
        });
      }
    });
  }
  
  getNotifications() {
    this.projectsService.getNotifications(this.token!, this.projectId!).subscribe({
      next: (res) => {
        this.notifications = {
          customIntervalMinutes: res.customIntervalMinutes,
          dailyTime: res.dailyTime,
          frequency: res.frequency,
          isEnabled: res.isEnabled
        };

        this.notificationForm.patchValue({
          frequency: res.customIntervalMinutes ?
            NotificationFrequency.CUSTOM_INTERVAL || null :
            NotificationFrequency[res.frequency as keyof typeof NotificationFrequency] || null,
          customIntervalMinutes: res.customIntervalMinutes || null,  
          dailyTime: res.dailyTime || null,
          isEnabled: res.isEnabled || null
        }, { emitEvent: false });
        
        if(this.sharedLinkId || !this.token) {
          this.notificationForm.controls['customIntervalMinutes'].disable();
          this.notificationForm.controls['dailyTime'].disable();
          this.notificationForm.controls['frequency'].disable();
          this.notificationForm.controls['isEnabled'].disable();
        }

      }
    });
  }
  
  getFrequencyLabel(frequency: NotificationFrequency): string {
    return `project-overview.${NotificationFrequency[frequency]}`;
  }
  
  get dailyTimeControl(): FormControl {
    return this.notificationForm.get('dailyTime') as FormControl;
  }
  
  isValueChanged(): boolean {
    return JSON.stringify(this.notifications) !== JSON.stringify(this.notificationForm.getRawValue());
  }

}
